import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  getCustomFieldDefinitions, createCustomFieldDefinition, updateCustomFieldDefinition,
  deleteCustomFieldDefinition, reorderCustomFieldDefinitions, getCustomFieldReferenceTables,
  getNativeFieldLayout, reorderNativeFieldLayout,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import ConfirmModal from "../components/UI/ConfirmModal";
import PageHeader from "../components/UI/PageHeader";
import { notify } from "../utils/notify";

const ReactGridLayout = WidthProvider(GridLayout);

// Same 12-column grid + width presets Dashboard.js and Modules.js already
// use for their own layout/width customization — kept identical so
// "width" means the same thing everywhere in the app.
const GRID_COLS = 12;
const ROW_HEIGHT = 34;
const CARD_H = 3;

const WIDTH_OPTIONS = [
  { value: 3, label: "Narrow (25%)" },
  { value: 4, label: "Small (33%)" },
  { value: 6, label: "Medium (50%)" },
  { value: 8, label: "Large (66%)" },
  { value: 12, label: "Full width" },
];

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes / No" },
  { value: "select", label: "Dropdown (custom options)" },
];

const FIELD_TYPE_BADGE = {
  text: "bg-blue-50 text-blue-600",
  number: "bg-purple-50 text-purple-600",
  date: "bg-amber-50 text-amber-600",
  boolean: "bg-teal-50 text-teal-600",
  select: "bg-pink-50 text-pink-600",
};

// Only "customer" is wired into any page's rendering today (see
// AddEditCustomer.js) — this list becomes real options once a second page
// is wired in; for now it's shown as a fixed badge rather than a dropdown
// with nothing to actually pick.
const ENTITY_TYPE = "customer";
const ENTITY_LABEL = "Customer";

// slugifyFieldKey mirrors custom_field_handler.go's own slugifyFieldKey
// EXACTLY (lowercase, every run of non alphanumeric characters collapsed
// to one underscore, leading/trailing underscores trimmed) — used here
// only to preview the key live while typing a new field's label; the
// backend's own computation on save is still the actual source of truth.
function slugifyFieldKey(label) {
  const s = (label || "").toLowerCase().trim();
  let out = "";
  let lastUnderscore = false;
  for (const ch of s) {
    if (/[a-z0-9]/.test(ch)) {
      out += ch;
      lastUnderscore = false;
    } else if (!lastUnderscore) {
      out += "_";
      lastUnderscore = true;
    }
  }
  return out.replace(/^_+|_+$/g, "");
}

// Packs a list of {w, h, ...} specs left-to-right, wrapping at GRID_COLS —
// the same bin-packing Dashboard.js uses to generate a sensible default
// arrangement before anyone has dragged anything.
function packItems(specs, startY = 0) {
  let cursorX = 0, cursorY = startY, rowMaxH = 0;
  const placed = [];
  for (const spec of specs) {
    const w = Math.max(1, Math.min(spec.w, GRID_COLS));
    if (cursorX + w > GRID_COLS) {
      cursorY += rowMaxH;
      cursorX = 0;
      rowMaxH = 0;
    }
    placed.push({ ...spec, x: cursorX, y: cursorY, w });
    cursorX += w;
    rowMaxH = Math.max(rowMaxH, spec.h);
  }
  return placed;
}

const OPTIONS_SOURCE_OPTIONS = [
  { value: "manual", label: "Manual list" },
  { value: "table", label: "From a table" },
];

const EMPTY_FORM = {
  label: "", key: "", field_type: "text", options: [""],
  is_multi: false, options_source: "manual", source_table: "", source_label_field: "", source_value_field: "",
  depends_on_definition_id: "", depends_on_filter_field: "",
  is_required: false, width: 12,
};

export default function CustomFields() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [items, setItems] = useState([]); // [{id, label, key, field_type, is_required, options, x, y, w, h}]
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);

  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = adding a new field
  const [fieldForm, setFieldForm] = useState(EMPTY_FORM);
  const [savingField, setSavingField] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null); // {id, label} | null
  const [deleteStatus, setDeleteStatus] = useState("idle");

  // referenceTables is the fixed catalog a "select" field can pull its
  // options from live instead of a manual list — [{key, label, fields:
  // [{key, label}]}] — loaded once, used to populate the Table/Label
  // field/Value field pickers in the Add/Edit Field modal below.
  const [referenceTables, setReferenceTables] = useState([]);
  useEffect(() => {
    getCustomFieldReferenceTables(token).then((tables) => setReferenceTables(tables || [])).catch(() => {});
  }, [token]);

  const selectedReferenceTable = referenceTables.find((t) => t.key === fieldForm.source_table);

  // Candidate "parent" fields a cascading dependency can filter against —
  // any OTHER single-select CUSTOM field already defined on this page
  // (manual or table-sourced: either way it resolves to one raw value to
  // filter by). A built-in field can never be a parent — it isn't a
  // dropdown at all — hence the !d.is_native guard.
  const dependencyCandidates = items.filter(
    (d) => !d.is_native && d.field_type === "select" && !d.is_multi && d.id !== editingId
  );

  // Loads BOTH this page's real Custom Fields and its built-in fields'
  // saved layout, then merges them into ONE canvas by sort_order — giving
  // full control over the whole form's arrangement from a single screen,
  // even though only Custom Fields can actually be added/edited/deleted
  // here (see the "Built-in" badge and missing Edit/Delete buttons below).
  // Each native item's id is a synthetic "native:<field_key>" string so it
  // never collides with a real definition's numeric id.
  const loadDefinitions = () => {
    setLoading(true);
    Promise.all([
      getCustomFieldDefinitions(token, ENTITY_TYPE),
      getNativeFieldLayout(token, ENTITY_TYPE),
    ])
      .then(([defs, natives]) => {
        const customSpecs = (defs || []).map((d) => ({ ...d, is_native: false, w: d.width || 12, h: CARD_H }));
        const nativeSpecs = (natives || []).map((n) => ({
          ...n, id: `native:${n.field_key}`, label: n.label, is_native: true, w: n.width || 12, h: CARD_H,
        }));
        const sorted = [...customSpecs, ...nativeSpecs].sort((a, b) => a.sort_order - b.sort_order);
        setItems(packItems(sorted, 0));
      })
      .catch(() => notify.error("Failed to load fields"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDefinitions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const layout = useMemo(
    () => items.map((it) => ({ i: String(it.id), x: it.x, y: it.y, w: it.w, h: it.h })),
    [items]
  );

  const handleLayoutChange = (newLayout) => {
    if (!editMode) return;
    setItems((prev) => prev.map((it) => {
      const match = newLayout.find((l) => l.i === String(it.id));
      return match ? { ...it, x: match.x, y: match.y, w: match.w, h: match.h } : it;
    }));
  };

  const handleSaveLayout = async () => {
    setSavingLayout(true);
    try {
      // Reading order top-to-bottom, then left-to-right, becomes the new
      // sort_order — the same order the field's own page (e.g.
      // AddEditCustomer.js) will render it in. The index is taken from
      // the FULL combined order (native fields included) so the two
      // separately-stored sort_order sequences still interleave back
      // into the exact same arrangement once that page merge-sorts them.
      const ordered = [...items].sort((a, b) => (a.y - b.y) || (a.x - b.x));
      const customItems = [];
      const nativeItems = [];
      ordered.forEach((it, idx) => {
        if (it.is_native) nativeItems.push({ field_key: it.field_key, sort_order: idx, width: it.w });
        else customItems.push({ id: it.id, sort_order: idx, width: it.w });
      });
      await Promise.all([
        reorderCustomFieldDefinitions(token, customItems),
        reorderNativeFieldLayout(token, ENTITY_TYPE, nativeItems),
      ]);
      notify.success("Layout saved");
      loadDefinitions();
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to save layout");
    } finally {
      setSavingLayout(false);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFieldForm(EMPTY_FORM);
    setShowFieldModal(true);
  };

  const openEditModal = (def) => {
    let options = [""];
    if (def.field_type === "select" && (def.options_source || "manual") === "manual") {
      try {
        const parsed = JSON.parse(def.options || "[]");
        if (parsed.length > 0) options = parsed;
      } catch { /* fall back to [""] */ }
    }
    setEditingId(def.id);
    setFieldForm({
      label: def.label,
      key: def.key,
      field_type: def.field_type,
      options,
      is_multi: def.is_multi || false,
      options_source: def.options_source || "manual",
      source_table: def.source_table || "",
      source_label_field: def.source_label_field || "",
      source_value_field: def.source_value_field || "",
      depends_on_definition_id: def.depends_on_definition_id ? String(def.depends_on_definition_id) : "",
      depends_on_filter_field: def.depends_on_filter_field || "",
      is_required: def.is_required,
      width: def.width || 12,
    });
    setShowFieldModal(true);
  };

  const updateOption = (idx, value) => {
    setFieldForm((prev) => ({ ...prev, options: prev.options.map((o, i) => (i === idx ? value : o)) }));
  };
  const addOption = () => setFieldForm((prev) => ({ ...prev, options: [...prev.options, ""] }));
  const removeOption = (idx) => setFieldForm((prev) => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));

  const handleSaveField = async () => {
    const label = fieldForm.label.trim();
    if (!label) {
      notify.error("Label is required");
      return;
    }
    const cleanOptions = fieldForm.options.map((o) => o.trim()).filter(Boolean);
    if (fieldForm.field_type === "select" && fieldForm.options_source === "manual" && cleanOptions.length === 0) {
      notify.error("A dropdown field needs at least one option");
      return;
    }
    if (fieldForm.field_type === "select" && fieldForm.options_source === "table") {
      if (!fieldForm.source_table || !fieldForm.source_label_field || !fieldForm.source_value_field) {
        notify.error("Pick a table, a name field, and a value field");
        return;
      }
      if (fieldForm.depends_on_definition_id && !fieldForm.depends_on_filter_field) {
        notify.error("Pick which column to filter by, or clear the dependency");
        return;
      }
    }

    setSavingField(true);
    try {
      const payload = {
        entity_type: ENTITY_TYPE,
        label,
        field_type: fieldForm.field_type,
        options: cleanOptions,
        is_multi: fieldForm.is_multi,
        options_source: fieldForm.options_source,
        source_table: fieldForm.source_table,
        source_label_field: fieldForm.source_label_field,
        source_value_field: fieldForm.source_value_field,
        depends_on_definition_id: fieldForm.depends_on_definition_id ? Number(fieldForm.depends_on_definition_id) : null,
        depends_on_filter_field: fieldForm.depends_on_definition_id ? fieldForm.depends_on_filter_field : "",
        is_required: fieldForm.is_required,
        width: fieldForm.width,
      };
      if (editingId) {
        await updateCustomFieldDefinition(token, editingId, payload);
        notify.success("Field updated");
      } else {
        await createCustomFieldDefinition(token, payload);
        notify.success("Field added");
      }
      setShowFieldModal(false);
      loadDefinitions();
    } catch (err) {
      notify.error(err.data?.error || err.message || "Failed to save field");
    } finally {
      setSavingField(false);
    }
  };

  const confirmDeleteField = async () => {
    if (!deleteTarget) return;
    setDeleteStatus("loading");
    try {
      await deleteCustomFieldDefinition(token, deleteTarget.id);
      setDeleteStatus("success");
      notify.success("Field deleted");
      setTimeout(() => {
        setDeleteTarget(null);
        setDeleteStatus("idle");
      }, 1000);
      loadDefinitions();
    } catch (err) {
      setDeleteStatus("error");
      notify.error(err.data?.error || err.message || "Failed to delete field");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Field"
        message={deleteTarget ? `Delete "${deleteTarget.label}"? Any values already saved for it will be deleted too.` : ""}
        onConfirm={confirmDeleteField}
        onCancel={() => setDeleteTarget(null)}
        isLoading={deleteStatus === "loading"}
        isSuccess={deleteStatus === "success"}
        isError={deleteStatus === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.CustomFields size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>Custom Fields</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">Add extra fields to a page, and arrange how they lay out</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <span className="px-3 py-2 rounded-xl bg-gray-50 text-gray-500 text-xs font-bold uppercase tracking-wide">
            Page: {ENTITY_LABEL}
          </span>
          <Button variant="primary" icon={ICONS.Plus} onClick={openAddModal} className="w-full sm:w-auto shrink-0">
            Add Field
          </Button>
        </div>
      </PageHeader>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm italic">
          Nothing to lay out for {ENTITY_LABEL} yet — click "Add Field" to create the first custom field.
        </div>
      ) : (
        <ReactGridLayout
          className="layout"
          layout={layout}
          cols={GRID_COLS}
          rowHeight={ROW_HEIGHT}
          margin={[16, 16]}
          containerPadding={[0, 0]}
          isDraggable={editMode}
          isResizable={editMode}
          resizeHandles={["e"]}
          compactType={editMode ? "vertical" : null}
          onLayoutChange={handleLayoutChange}
        >
          {items.map((def) => (
            <div key={def.id}>
              <div className="h-full w-full bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between p-4 overflow-hidden">
                <div className="min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-bold text-gray-800 text-sm truncate">{def.label}</h3>
                    {def.is_required && <span className="text-red-400 text-xs font-bold shrink-0">Required</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {def.is_native ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-500">Built-in</span>
                    ) : (
                      <>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${FIELD_TYPE_BADGE[def.field_type] || "bg-gray-50 text-gray-500"}`}>
                          {FIELD_TYPE_OPTIONS.find((o) => o.value === def.field_type)?.label || def.field_type}
                        </span>
                        {def.field_type === "select" && def.is_multi && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-indigo-50 text-indigo-600">Multi</span>
                        )}
                        {def.field_type === "select" && def.options_source === "table" && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-orange-50 text-orange-600">
                            {referenceTables.find((t) => t.key === def.source_table)?.label || def.source_table}
                          </span>
                        )}
                        {def.depends_on_definition_id && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-cyan-50 text-cyan-600">
                            ↳ {items.find((i) => i.id === def.depends_on_definition_id)?.label || "field"}
                          </span>
                        )}
                      </>
                    )}
                    <span className="text-[10px] text-gray-300 font-mono truncate">{def.is_native ? def.field_key : def.key}</span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 no-drag">
                  {def.is_native ? (
                    <span className="text-[11px] text-gray-300 italic px-1">Position &amp; width only — can't be edited or removed</span>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => openEditModal(def)}>Edit</Button>
                      <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => setDeleteTarget({ id: def.id, label: def.label })}>Delete</Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </ReactGridLayout>
      )}

      {items.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-3 mt-4">
          <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
            <ICONS.CustomFields className="w-4 h-4" />
            {editMode ? "Drag to reorder, or drag the right edge to resize a field's width." : "This is how these fields will lay out on their page."}
          </div>
          <div className="flex items-center gap-2">
            {editMode && (
              <button
                onClick={handleSaveLayout}
                disabled={savingLayout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-white text-xs font-bold shadow-lg transition-transform hover:scale-105 disabled:opacity-60"
                style={{ backgroundColor: "var(--primary-color)" }}
              >
                {savingLayout ? "Saving..." : "Save Layout"}
              </button>
            )}
            <button
              onClick={() => setEditMode((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-colors ${editMode ? "bg-gray-800 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
            >
              {editMode ? "Done" : "Customize Layout"}
            </button>
          </div>
        </div>
      )}

      {showFieldModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4">{editingId ? "Edit Field" : "Add Field"}</h3>

            <div className="space-y-4">
              <div>
                <FloatingInput
                  label="Label"
                  value={fieldForm.label}
                  onChange={(e) => setFieldForm((prev) => ({ ...prev, label: e.target.value }))}
                />
                {editingId ? (
                  <p className="text-[10px] text-gray-400 font-mono mt-1 ms-2">key: {fieldForm.key} (fixed — the label can change freely without affecting it)</p>
                ) : (
                  fieldForm.label.trim() && (
                    <p className="text-[10px] text-gray-400 font-mono mt-1 ms-2">key: {slugifyFieldKey(fieldForm.label) || "—"}</p>
                  )
                )}
              </div>

              <SelectBox
                label="Field Type"
                options={FIELD_TYPE_OPTIONS}
                value={fieldForm.field_type}
                onChange={(val) => setFieldForm((prev) => ({ ...prev, field_type: val }))}
              />

              {fieldForm.field_type === "select" && (
                <div className="space-y-4 pt-2 border-t border-gray-50">
                  <ToggleSwitch
                    label="Allow selecting more than one option"
                    checked={fieldForm.is_multi}
                    onChange={(val) => setFieldForm((prev) => ({ ...prev, is_multi: val }))}
                  />

                  <SelectBox
                    label="Options Source"
                    options={OPTIONS_SOURCE_OPTIONS}
                    value={fieldForm.options_source}
                    onChange={(val) => setFieldForm((prev) => ({ ...prev, options_source: val }))}
                  />

                  {fieldForm.options_source === "manual" ? (
                    <div className="space-y-2">
                      <p className="text-[11px] text-gray-400">Options shown in the dropdown.</p>
                      {fieldForm.options.map((opt, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => updateOption(idx, e.target.value)}
                            className="flex-1 rounded-xl border-2 border-gray-100 bg-gray-50/50 px-3 py-2 text-sm focus:outline-none focus:border-[var(--primary-color)]"
                            placeholder={`Option ${idx + 1}`}
                          />
                          {fieldForm.options.length > 1 && (
                            <button type="button" onClick={() => removeOption(idx)} className="text-gray-400 hover:text-red-500 p-1">
                              <ICONS.Close className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button type="button" onClick={addOption} className="text-xs font-bold text-[var(--primary-color)] hover:underline">+ Add option</button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-[11px] text-gray-400">
                        Options are pulled live from an existing table — e.g. every country, kept in sync automatically.
                      </p>
                      <SelectBox
                        label="Table"
                        options={referenceTables.map((t) => ({ value: t.key, label: t.label }))}
                        value={fieldForm.source_table}
                        onChange={(val) => setFieldForm((prev) => ({
                          ...prev, source_table: val, source_label_field: "", source_value_field: "",
                          depends_on_definition_id: "", depends_on_filter_field: "",
                        }))}
                      />
                      {selectedReferenceTable && (
                        <div className="grid grid-cols-2 gap-3">
                          <SelectBox
                            label="Name shown"
                            options={selectedReferenceTable.fields.map((f) => ({ value: f.key, label: f.label }))}
                            value={fieldForm.source_label_field}
                            onChange={(val) => setFieldForm((prev) => ({ ...prev, source_label_field: val }))}
                          />
                          <SelectBox
                            label="Value stored"
                            options={selectedReferenceTable.fields.map((f) => ({ value: f.key, label: f.label }))}
                            value={fieldForm.source_value_field}
                            onChange={(val) => setFieldForm((prev) => ({ ...prev, source_value_field: val }))}
                          />
                        </div>
                      )}

                      {selectedReferenceTable && (selectedReferenceTable.filter_fields || []).length > 0 && (
                        <div className="space-y-3 pt-2 border-t border-gray-50">
                          <p className="text-[11px] text-gray-400">
                            Optional: narrow this field's options based on another field already on this page — e.g. only show Zones belonging to the Country picked above it.
                          </p>
                          <SelectBox
                            label="Depends on field"
                            options={[
                              { value: "", label: "No dependency — show every option" },
                              ...dependencyCandidates.map((d) => ({ value: String(d.id), label: d.label })),
                            ]}
                            value={fieldForm.depends_on_definition_id}
                            onChange={(val) => setFieldForm((prev) => ({
                              ...prev,
                              depends_on_definition_id: val,
                              depends_on_filter_field: val ? prev.depends_on_filter_field : "",
                            }))}
                          />
                          {fieldForm.depends_on_definition_id && (
                            <SelectBox
                              label={`Filter ${selectedReferenceTable.label} by`}
                              options={selectedReferenceTable.filter_fields.map((f) => ({ value: f.key, label: f.label }))}
                              value={fieldForm.depends_on_filter_field}
                              onChange={(val) => setFieldForm((prev) => ({ ...prev, depends_on_filter_field: val }))}
                            />
                          )}
                          {fieldForm.depends_on_definition_id && fieldForm.depends_on_filter_field && (
                            <p className="text-[10px] text-gray-400 ms-1">
                              Make sure the "Value stored" of the parent field above actually matches {selectedReferenceTable.label}.{fieldForm.depends_on_filter_field} (e.g. both should be an ID).
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <ToggleSwitch
                label="Required"
                checked={fieldForm.is_required}
                onChange={(val) => setFieldForm((prev) => ({ ...prev, is_required: val }))}
              />

              <SelectBox
                label="Width"
                options={WIDTH_OPTIONS}
                value={fieldForm.width}
                onChange={(val) => setFieldForm((prev) => ({ ...prev, width: val }))}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowFieldModal(false)} className="flex-1" disabled={savingField}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveField} loading={savingField} className="flex-1">
                {editingId ? "Save Changes" : "Add Field"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
